import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MobileHeader from '../components/MobileHeader';
import { Button } from '../components/ui/button';
import { TrendingUp, TrendingDown, DollarSign, Activity, Wallet, ArrowUpRight, ArrowDownRight, ArrowDownUp, Repeat, Shield, ChevronRight } from 'lucide-react';
import { mockUserPortfolio } from '../mockData';
import { useAuth } from '../context/AuthContext';
import { useCryptoData } from '../hooks/useCryptoData';
import { useUnifiedBalance } from '../hooks/useUnifiedBalance';
import BalanceDisplay, { BalanceText } from '../components/ui/BalanceDisplay';
import CryptoIcon from '../components/CryptoIcon';
import Sparkline from '../components/Sparkline';
import CopyDepositAddressCard from '../components/CopyDepositAddressCard';
import StreakBadge, { computeWinStreak } from '../components/StreakBadge';
import api from '../services/api';

import { fmtFixed } from '../utils/format';
const Dashboard = () => {
  const { user } = useAuth();
  const { totalBalance, availableBalance, holdings, loading: balanceLoading, refresh: refreshBalance } = useUnifiedBalance();
  const [portfolio] = useState(mockUserPortfolio);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [momentum, setMomentum] = useState(null);
  const { cryptos, loading } = useCryptoData(20);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [kycSettings, setKycSettings] = useState({ kyc_required: false });
  const [kycStatus, setKycStatus] = useState(null);

  // Fetch real transactions + momentum
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [txRes, mRes] = await Promise.all([
          api.get('/dashboard/recent-transactions?limit=8'),
          api.get('/dashboard/momentum'),
        ]);
        if (cancelled) return;
        setTransactions(txRes.data?.transactions || []);
        setMomentum(mRes.data || null);
      } catch {
        /* silent — dashboard degrades gracefully */
      } finally {
        if (!cancelled) setTxLoading(false);
      }
    };
    load();
    // Keep active-trade count, PnL and streak current as trades start/finish
    window.addEventListener('phantomtrades:trade-settled', load);
    window.addEventListener('phantomtrades:trade-placed', load);
    return () => {
      cancelled = true;
      window.removeEventListener('phantomtrades:trade-settled', load);
      window.removeEventListener('phantomtrades:trade-placed', load);
    };
  }, []);

  // Fetch KYC settings + user's status (independent of the transactions call).
  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, statusRes] = await Promise.all([
          api.get('/kyc/settings'),
          api.get('/kyc/status').catch(() => ({ data: null })),
        ]);
        setKycSettings(settingsRes.data || { kyc_required: false });
        setKycStatus(statusRes.data || null);
      } catch {
        /* silent */
      }
    })();
  }, []);

  // Handle balance refresh
  const handleRefreshBalance = async () => {
    setIsRefreshing(true);
    try {
      await refreshBalance();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const activeTradesCount = transactions.filter(
    (t) => t.type === 'binary_trade' && t.status === 'active'
  ).length;

  // Total lifetime profit = sum of positive trade PnL - abs of losses across all
  // fetched transactions (matches how backend momentum aggregates day buckets)
  const totalProfit = transactions.reduce((sum, t) => {
    if (t.type !== 'binary_trade') return sum;
    if (t.status === 'won' || t.status === 'lost') return sum + (t.amount || 0);
    return sum;
  }, 0);

  const winStreak = computeWinStreak(transactions);

  return (
    <div className="min-h-screen overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
      <Navbar isDashboard={true} />
      <MobileHeader isDashboard={true} />

      <div className="pt-20 sm:pt-24 pb-20 sm:pb-12 px-3 sm:px-4 md:px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Welcome Section */}
          <div className="mb-6 sm:mb-8 min-w-0">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 break-words">Welcome back, <span className="gradient-text">{user?.name}</span></h1>
            <p className="text-gray-400 text-sm sm:text-base md:text-lg">Here's what's happening with your portfolio today</p>
          </div>

          {/* Copy USDT Deposit Address — mobile only */}
          <CopyDepositAddressCard />

          {/* KYC Application Card — only for users who haven't submitted */}
          {kycStatus && (kycStatus.kyc_status === 'not_submitted' || !kycStatus.kyc_status) && (
            <Link
              to="/kyc"
              data-testid="dashboard-kyc-card"
              className="block mb-6 rounded-2xl border transition-all hover:scale-[1.01] hover:border-purple-400/60"
              style={{
                background: kycSettings.kyc_required
                  ? 'linear-gradient(90deg, rgba(139,92,246,0.15), rgba(45,212,191,0.10))'
                  : 'rgba(139,92,246,0.08)',
                borderColor: kycSettings.kyc_required ? 'rgba(139,92,246,0.5)' : 'rgba(139,92,246,0.25)',
              }}
            >
              <div className="flex items-center gap-4 p-4 sm:p-5">
                <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-purple-500/25 flex items-center justify-center shrink-0">
                  <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-purple-200" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm sm:text-base">
                    {kycSettings.kyc_required ? 'Verify your identity to unlock trading' : 'Complete KYC to lift limits'}
                    {kycSettings.kyc_required && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400/20 text-amber-200">REQUIRED</span>
                    )}
                  </p>
                  <p className="text-gray-400 text-xs sm:text-sm mt-0.5 truncate">
                    Takes 2 minutes • Review usually within 24 hours
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
              </div>
            </Link>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="glow-card p-4 sm:p-6 rounded-2xl min-w-0">
              <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
                <div className="p-2.5 sm:p-3 bg-gradient-to-br from-purple-600 to-blue-500 rounded-xl shrink-0">
                  <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <Sparkline
                  data={momentum?.balance_history || []}
                  color="#a855f7"
                  width={72}
                  height={28}
                  ariaLabel="7-day balance trend"
                />
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mb-1">Account Balance</p>
              <BalanceDisplay 
                balance={availableBalance} 
                size="medium" 
                className="justify-start" 
                storageKey="dashboard_balance_visibility" 
                currency="USDT"
                showRefresh={true}
                onRefresh={handleRefreshBalance}
                isRefreshing={isRefreshing}
              />
            </div>

            <div className="glow-card p-4 sm:p-6 rounded-2xl min-w-0">
              <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
                <div className="p-2.5 sm:p-3 bg-gradient-to-br from-green-600 to-emerald-500 rounded-xl shrink-0">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <Sparkline
                  data={momentum?.profit_history || []}
                  color="#34d399"
                  width={72}
                  height={28}
                  ariaLabel="7-day profit trend"
                />
              </div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <p className="text-gray-400 text-xs sm:text-sm">Total Profit</p>
                <StreakBadge streak={winStreak} />
              </div>
              <BalanceDisplay 
                balance={totalProfit} 
                size="medium" 
                className={`justify-start ${totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}
                symbol={totalProfit >= 0 ? '+' : ''}
                currency="USDT" 
                storageKey="dashboard_balance_visibility"
                showRefresh={false}
              />
            </div>

            <div className="glow-card p-4 sm:p-6 rounded-2xl min-w-0">
              <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
                <div className="p-2.5 sm:p-3 bg-gradient-to-br from-orange-600 to-red-500 rounded-xl shrink-0">
                  <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <Sparkline
                  data={momentum?.trades_history || []}
                  color="#fb923c"
                  width={72}
                  height={28}
                  ariaLabel="7-day trades activity"
                />
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mb-1">Active Trades</p>
              <h3 className="text-2xl sm:text-3xl font-bold" data-testid="stat-active-trades">{activeTradesCount}</h3>
            </div>

            <div className="glow-card p-4 sm:p-6 rounded-2xl min-w-0">
              <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
                <div className="p-2.5 sm:p-3 bg-gradient-to-br from-cyan-600 to-blue-500 rounded-xl shrink-0">
                  <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <Sparkline
                  data={momentum?.assets_history || []}
                  color="#22d3ee"
                  width={72}
                  height={28}
                  ariaLabel="7-day assets trend"
                />
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mb-1">Assets</p>
              <h3 className="text-2xl sm:text-3xl font-bold" data-testid="stat-assets-count">{holdings.length || 0}</h3>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Portfolio Holdings */}
            <div className="lg:col-span-2 min-w-0">
              <div className="glow-card p-4 sm:p-6 rounded-2xl">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Your Holdings</h2>
                <div className="space-y-3 sm:space-y-4">
                  {balanceLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent mx-auto"></div>
                    </div>
                  ) : holdings.length > 0 ? (
                    holdings.map((holding, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-gray-900/50 rounded-xl hover:bg-gray-900/70 transition-colors"
                        data-testid={`holding-row-${holding.crypto_symbol}`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                          <CryptoIcon
                            symbol={holding.crypto_symbol}
                            size={40}
                            className="shrink-0 sm:!w-12 sm:!h-12"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-base sm:text-lg truncate">
                              {holding.crypto_name}
                            </p>
                            <p className="text-gray-400 text-xs sm:text-sm truncate">
                              {fmtFixed((holding.balance || 0), 8)} {holding.crypto_symbol}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <BalanceDisplay
                            balance={holding.usdt_value || 0}
                            size="small"
                            className="justify-end text-base sm:text-lg"
                            showToggle={false}
                            showRefresh={false}
                            storageKey="dashboard_balance_visibility"
                          />
                          <p className="text-xs sm:text-sm text-gray-400">
                            ${(holding.price || holding.price_usdt || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <Wallet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No holdings yet</p>
                      <Link to="/wallet" className="text-purple-400 hover:underline text-sm mt-2 inline-block">
                        Add funds to get started
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Market Overview */}
            <div className="lg:col-span-1 min-w-0">
              <div className="glow-card p-6 rounded-2xl mb-6">
                <h2 className="text-2xl font-bold mb-6">Market Overview</h2>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="flex items-center justify-between animate-pulse">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-700 rounded-full"></div>
                          <div>
                            <div className="h-4 w-12 bg-gray-700 rounded"></div>
                            <div className="h-3 w-20 bg-gray-700 rounded mt-1"></div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="h-4 w-16 bg-gray-700 rounded"></div>
                          <div className="h-3 w-12 bg-gray-700 rounded mt-1"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                    {cryptos.slice(0, 20).map((crypto, index) => (
                      <div key={index} className="flex items-center justify-between hover:bg-gray-900/30 p-2 rounded-lg transition-colors">
                        <div className="flex items-center space-x-3">
                          <img src={crypto.image} alt={crypto.name} className="w-8 h-8 rounded-full" />
                          <div>
                            <p className="font-semibold">{crypto.symbol.toUpperCase()}</p>
                            <p className="text-xs text-gray-400">{crypto.name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">${crypto.current_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          <p className={`text-xs flex items-center justify-end ${crypto.price_change_percentage_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {crypto.price_change_percentage_24h >= 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
                            {crypto.price_change_percentage_24h >= 0 ? '+' : ''}{crypto.price_change_percentage_24h.toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Swap & Recent Transactions */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Swap */}
            <div className="lg:col-span-1 min-w-0">
              <div className="glow-card p-6 rounded-2xl">
                <h2 className="text-xl font-bold mb-4">Quick Swap</h2>
                <p className="text-sm text-gray-400 mb-4">Instantly swap between your holdings</p>
                <Link to="/swap">
                  <Button className="w-full neo-btn neo-btn-primary font-semibold">
                    <ArrowDownUp className="mr-2 h-4 w-4" />
                    Open Swap
                  </Button>
                </Link>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Popular Pairs</span>
                  </div>
                  <div className="space-y-1">
                    {['BTC/ETH', 'ETH/BNB', 'SOL/BTC'].map((pair, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-900/50 rounded-lg text-xs">
                        <span className="text-gray-300">{pair}</span>
                        <span className="text-purple-400">0.1% fee</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="lg:col-span-2 min-w-0">
              <div className="glow-card p-4 sm:p-6 rounded-2xl">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Recent Transactions</h2>
              <div className="space-y-3" data-testid="recent-transactions-list">
                {txLoading && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent mx-auto"></div>
                  </div>
                )}

                {!txLoading && transactions.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No activity yet</p>
                    <p className="text-sm mt-1">Your trades and deposits will show up here.</p>
                  </div>
                )}

                {!txLoading && transactions.map((tx) => {
                  const positive = (tx.amount || 0) >= 0;
                  const isDeposit = tx.type === 'deposit';
                  const isWithdrawal = tx.type === 'withdrawal';
                  const isTrade = tx.type === 'binary_trade';

                  let Icon;
                  let iconBg;
                  let iconColor;
                  if (isDeposit) {
                    Icon = ArrowDownRight; iconBg = 'bg-green-600/20'; iconColor = 'text-green-400';
                  } else if (isWithdrawal) {
                    Icon = ArrowUpRight; iconBg = 'bg-red-600/20'; iconColor = 'text-red-400';
                  } else {
                    Icon = Repeat; iconBg = positive ? 'bg-green-600/20' : 'bg-red-600/20'; iconColor = positive ? 'text-green-400' : 'text-red-400';
                  }

                  const label = (() => {
                    if (isDeposit) return `Deposit ${tx.symbol}`;
                    if (isWithdrawal) return `Withdraw ${tx.symbol}`;
                    if (isTrade) {
                      const dir = (tx.direction || '').toUpperCase();
                      return `${tx.symbol}${dir ? ` ${dir}` : ''}`;
                    }
                    return tx.type;
                  })();

                  const statusStyle = tx.status === 'won' || tx.status === 'completed' || tx.status === 'approved'
                    ? 'bg-green-600/20 text-green-400'
                    : tx.status === 'lost' || tx.status === 'rejected' || tx.status === 'failed'
                    ? 'bg-red-600/20 text-red-400'
                    : 'bg-yellow-600/20 text-yellow-400';

                  const when = tx.timestamp ? new Date(tx.timestamp).toLocaleDateString() : '';

                  return (
                    <div key={tx.id} className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-gray-900/50 rounded-xl hover:bg-gray-900/70 transition-colors" data-testid={`tx-row-${tx.id}`}>
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                        <div className={`shrink-0 p-2.5 sm:p-3 rounded-xl ${iconBg}`}>
                          <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${iconColor}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold capitalize truncate">{label}</p>
                          <p className="text-gray-400 text-xs sm:text-sm truncate">{when}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-semibold text-sm sm:text-base ${positive ? 'text-green-400' : 'text-red-400'}`}>
                          {positive ? '+' : ''}{(tx.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {tx.currency || 'USDT'}
                        </p>
                        <p className="text-sm">
                          <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${statusStyle}`}>
                            {tx.status}
                          </span>
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
